import { useState, useEffect, useRef, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';

/** businesses.geofence as stored: `polygon` holds GeoJSON Polygon coordinates
 *  (rings of [lng, lat]). This is the shape the consumer app and live check-in
 *  read — the map editor's GeoJSON geometry is converted to/from it. */
export interface StoredGeofence {
  geometry: { polygon: number[][][] };
  geofenceId?: string;
}

export interface Business {
  id: string;
  name: string;
  email: string | null;
  phone_number: string | null;
  address: string | null;
  location_lat: number | null;
  location_lng: number | null;
  timezone: string | null;
  capacity: number | null;
  business_logo: string | null;
  verified: boolean;
  business_type: string | null;
  geofence?: StoredGeofence | null;
  created_at: string;
  updated_at: string;
}

interface BusinessesResponse {
  items: Business[];
  count: number;
  total: number;
}

export interface UseBusinessesOptions {
  search?: string;
  page?: number;
  limit?: number;
  apiUrl?: string;
  /** Comma-separated list of columns to return (e.g. "id,name,location_lat,location_lng"). Omit to return all columns. */
  fields?: string;
}

interface UseBusinessesResult {
  businesses: Business[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useBusinesses(opts: UseBusinessesOptions = {}): UseBusinessesResult {
  const { search = '', page = 0, limit = 25, apiUrl, fields } = opts;
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const [refreshTick, setRefreshTick] = useState(0);

  const refetch = useCallback(() => setRefreshTick(t => t + 1), []);

  useEffect(() => {
    let isMounted = true;

    const fetchBusinesses = async () => {
      const url = apiUrl || process.env.REACT_APP_API_URL;
      if (!url) {
        if (isMounted) { setError('REACT_APP_API_URL is not set'); setLoading(false); }
        return;
      }

      if (isMounted) setLoading(true);

      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }

        const offset = page * limit;
        const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
        if (search) params.set('search', search);
        if (fields) params.set('fields', fields);

        const response = await fetch(`${url}/admin/businesses?${params}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const data: BusinessesResponse = await response.json();
        if (!response.ok) throw new Error((data as any).error || `HTTP ${response.status}`);

        if (isMounted) {
          setBusinesses(data.items);
          setTotal(data.total);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) { setError(err?.message ?? 'Unknown error'); setBusinesses([]); }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchBusinesses();
    return () => { isMounted = false; };
  }, [isAuthenticated, instance, search, page, limit, fields, refreshTick, apiUrl]);

  return { businesses, total, loading, error, refetch };
}
